import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { configureClock } from '@/lib/clock';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);
export const GROUP_PREFIX = { finanzas: 'fin_', fin_rentas: 'fin_rentas_', contratos: 'con_', propiedades: 'prop_', inquilinos: 'inq_', mantenimiento: 'mant_' };

// Ruta de la app → módulo de la matriz de permisos
export const MODULE_BY_PATH = [
  ['/app/finances', 'finanzas'], ['/app/payments', 'finanzas'], ['/app/fixed-expenses', 'finanzas'],
  ['/app/contracts', 'contratos'], ['/app/occupancy', 'ocupacion'], ['/app/properties/occupancy', 'ocupacion'],
  ['/app/properties', 'propiedades'], ['/app/tenants', 'inquilinos'], ['/app/employees', 'empleados'],
  ['/app/maintenance', 'mantenimiento'], ['/app/external-services', 'servicios_externos'], ['/app/review', 'verificar'], ['/app/tasks', 'tareas'],
];

export const PermissionsProvider = ({ children }) => {
  const { session, mfaRequired } = useAuth();
  const [me, setMe] = useState(null);
  const token = session?.access_token;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me', { noCache: true, silent: true });
      configureClock({ timezone: data?.timezone, serverNow: data?.server_now });
      setMe(data);
    } catch {
      setMe((prev) => prev || { org_role: null, permissions: {} });
    }
  }, []);

  useEffect(() => {
    if (!token || mfaRequired) { setMe(null); return; }
    refresh();
  }, [token, mfaRequired, refresh]);

  const value = useMemo(() => {
    const perms = me?.permissions || {};
    // Un grupo ('finanzas', 'contratos', 'propiedades', 'inquilinos', 'mantenimiento') es alias de sus subsecciones: basta con que alguna lo permita
    const can = (module, action = 'ver') => {
      const prefix = GROUP_PREFIX[module];
      if (prefix) return Object.keys(perms).some((k) => k.startsWith(prefix) && perms[k][action]);
      return !!(perms[module] && perms[module][action]);
    };
    return { ready: !!me, role: me?.org_role || null, isSuperAdmin: me?.org_role === 'super_admin', permissions: perms, can, refresh, me };
  }, [me, refresh]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within a PermissionsProvider');
  return ctx;
};

// Renderiza children solo si el rol tiene el permiso (oculta por completo si no)
export const Can = ({ module, action = 'ver', children, fallback = null }) => {
  const { can } = usePermissions();
  return can(module, action) ? children : fallback;
};

export default PermissionsContext;
